import React from "react"

import { Building2, Clock, Loader2, MapPin, Plus, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { CustomerStatus, CustomerType, SlotType } from "@/types/collections"

import DayToggles from "./DayToggles"
import SamsaraLocationFields from "./SamsaraLocationFields"
import { BulkCreateState, blankSlot } from "./useBulkCreate"

const BulkCreateDialog: React.FC<{ bulk: BulkCreateState }> = ({ bulk }) => {
  const {
    open,
    setOpen,
    includeLocation,
    setIncludeLocation,
    includeSlots,
    setIncludeSlots,
    customerForm,
    setCustomerForm,
    locationForm,
    setLocationForm,
    slotForms,
    setSlotForms,
    samsaraFailed,
    setSamsaraFailed,
    create,
    isFormValid,
    isSaving,
  } = bulk
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Create</DialogTitle>
          <DialogDescription>
            Create a customer with optional location and slots in one go
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <h4 className="font-medium text-palette-jungleGreen900 flex items-center gap-2">
              <Building2 size={16} />
              Customer Details
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Name *</Label>
                <Input
                  value={customerForm.name || ""}
                  onChange={(e) =>
                    setCustomerForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="bg-white"
                />
              </div>
              <div className="grid gap-2">
                <Label>Account ID *</Label>
                <Input
                  value={customerForm.account_id || ""}
                  onChange={(e) =>
                    setCustomerForm((prev) => ({ ...prev, account_id: e.target.value }))
                  }
                  className="bg-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select
                  value={customerForm.customer_type}
                  onValueChange={(value: CustomerType) =>
                    setCustomerForm((prev) => ({ ...prev, customer_type: value }))
                  }
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="shipper">Shipper</SelectItem>
                    <SelectItem value="pudo">PUDO</SelectItem>
                    <SelectItem value="haulage">Haulage</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select
                  value={customerForm.status}
                  onValueChange={(value: CustomerStatus) =>
                    setCustomerForm((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-palette-jungleGreen900 flex items-center gap-2">
                <MapPin size={16} />
                Location Details
              </h4>
              <Switch checked={includeLocation} onCheckedChange={setIncludeLocation} />
            </div>
            {includeLocation && (
              <div className="space-y-4 pl-4 border-l-2 border-palette-jungleGreen200">
                <div className="grid gap-2">
                  <Label>Site Name *</Label>
                  <Input
                    value={locationForm.site_name || ""}
                    onChange={(e) =>
                      setLocationForm((prev) => ({ ...prev, site_name: e.target.value }))
                    }
                    className="bg-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={locationForm.contact_name || ""}
                      onChange={(e) =>
                        setLocationForm((prev) => ({ ...prev, contact_name: e.target.value }))
                      }
                      className="bg-white"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Contact Phone</Label>
                    <Input
                      type="tel"
                      value={locationForm.contact_phone || ""}
                      onChange={(e) =>
                        setLocationForm((prev) => ({
                          ...prev,
                          contact_phone: e.target.value,
                        }))
                      }
                      className="bg-white"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label>Address *</Label>
                  <Input
                    value={locationForm.address || ""}
                    onChange={(e) =>
                      setLocationForm((prev) => ({ ...prev, address: e.target.value }))
                    }
                    className="bg-white"
                  />
                </div>
                <SamsaraLocationFields
                  variant="bulk"
                  form={locationForm}
                  setForm={setLocationForm}
                  samsaraFailed={samsaraFailed}
                  setSamsaraFailed={setSamsaraFailed}
                />
              </div>
            )}
          </div>

          {includeLocation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-palette-jungleGreen900 flex items-center gap-2">
                  <Clock size={16} />
                  Slots
                </h4>
                <Switch checked={includeSlots} onCheckedChange={setIncludeSlots} />
              </div>
              {includeSlots && (
                <div className="space-y-4 pl-4 border-l-2 border-palette-jungleGreen200">
                  {slotForms.map((slot, index) => (
                    <div
                      key={index}
                      className="space-y-3 p-3 border rounded bg-palette-jungleGreen000"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Slot {index + 1}</span>
                        {slotForms.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setSlotForms((prev) => prev.filter((_, i) => i !== index))
                            }
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-2">
                        <Label className="text-xs">Days *</Label>
                        <DayToggles
                          compact
                          value={slot.days || []}
                          onChange={(days) =>
                            setSlotForms((prev) =>
                              prev.map((s, i) => (i === index ? { ...s, days } : s)),
                            )
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-2">
                          <Label className="text-xs">Start Time *</Label>
                          <Input
                            type="time"
                            value={slot.start_time || ""}
                            onChange={(e) =>
                              setSlotForms((prev) =>
                                prev.map((s, i) =>
                                  i === index ? { ...s, start_time: e.target.value } : s,
                                ),
                              )
                            }
                            className="bg-white h-8 text-sm"
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label className="text-xs">End Time *</Label>
                          <Input
                            type="time"
                            value={slot.end_time || ""}
                            onChange={(e) =>
                              setSlotForms((prev) =>
                                prev.map((s, i) =>
                                  i === index ? { ...s, end_time: e.target.value } : s,
                                ),
                              )
                            }
                            className="bg-white h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid gap-2">
                        <Label className="text-xs">Slot Type</Label>
                        <Select
                          value={slot.slot_type}
                          onValueChange={(value: SlotType) =>
                            setSlotForms((prev) =>
                              prev.map((s, i) => (i === index ? { ...s, slot_type: value } : s)),
                            )
                          }
                        >
                          <SelectTrigger className="bg-white h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="shipper_collection">Shipper Collection</SelectItem>
                            <SelectItem value="internal_stop">Internal Stop</SelectItem>
                            <SelectItem value="haulage_collection">Haulage Collection</SelectItem>
                            <SelectItem value="haulage_delivery">Haulage Delivery</SelectItem>
                            <SelectItem value="depot_injection">Depot Injection</SelectItem>
                            <SelectItem value="direct_injection">Direct Injection</SelectItem>
                            <SelectItem value="charging_stop">Charging Stop</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSlotForms((prev) => [...prev, blankSlot()])
                    }
                    className="w-full h-8"
                  >
                    <Plus size={14} className="mr-1" />
                    Add Slot
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={create}
            disabled={!isFormValid() || isSaving}
            variant="confirm"
          >
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default BulkCreateDialog
